import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, SelectOption, Toggle } from '@kaleyra/emmy';
import cn from 'classnames';
import ErrorIcon from '../../../assets/error.png';
import CloseIcon from '../../../assets/CloseIcon.svg';
import AddIcon from '../../../assets/addV.png';
import AddIconDisabled from '../../../assets/AddIcon.svg';

import { algorithms } from '../constants';
import { validateIV, validateKey } from '../encryption-utils';

import Styles from './EncProfile.module.scss';
import { useToggle } from '../hooks/use-toggle';
import ErrorImage from '../../../components/ErrorImage/ErrorImage';

export interface EncryptionData {
	algorithm?: {
		touched?: boolean;
		data?: string;
	};
	iv?: {
		touched?: boolean;
		data?: string;
	};
	key?: {
		touched?: boolean;
		data?: string;
	};
}

interface EncProfileProps {
	parameters: string[];
	setParameters: (
		parameters: string[] | ((prev: string[]) => string[])
	) => void;
	requestTestError: Record<string,unknown>
	setRequestTestError: (testError:Record<string,unknown>) => void;
	encryptionData: EncryptionData;
	setEncryptionData: (value: EncryptionData) => void;
}

interface EnabledContentProps {
	encryptionData: EncryptionData;
	setEncryptionData: (encryptionData: EncryptionData) => void;
	parameters: string[];
	requestTestError: Record<string,unknown>;
	setRequestTestError: (testError:Record<string,unknown>) => void;
	setParameters: (
		parameters: string[] | ((prev: string[]) => string[])
	) => void;
}

interface ParamsToEncrypt {
	value: string;
	index: number;
	setParameters: (
		parameters: string[] | ((prev: string[]) => string[])
	) => void;
}

const EncProfile: React.FC<EncProfileProps> = ({
	encryptionData,
	setEncryptionData,
	requestTestError,
	setRequestTestError,
	parameters,
	setParameters,
}) => {
	const { t } = useTranslation();

	// Initialize toogle based on the encryptionData
	// If there is no encryptionData, then it is switched off
	const [status, toggle] = useToggle(Object.keys(encryptionData).length > 0);

	// Reset encryption data or parameters if the toggle is off
	React.useEffect(() => {
		if (!status) {
			setEncryptionData({});
			setParameters([]);
		}
	}, [setEncryptionData, setParameters, status]);

	return (
		<div>
			<div className={Styles.containerEncryptionText}>
				<div>
					<div className={Styles.compHeader}>
						{t('enableEncryption')}
					</div>
					<p className={Styles.descriptionText}>
						{t('encryptDataAlgorithm')}
						<a
							href="https://developers.kaleyra.io/docs/callback-profiles-in-accountssettings#callback-encryption"
							className={Styles.link}
							target="_blank"
							rel="noopener noreferrer"
						>
							{t('viewDocumentation')}
						</a>
					</p>
				</div>
				<div>
					<Toggle onClick={toggle} checked={status} />
				</div>
			</div>
			{status && (
				<EnabledContent
					encryptionData={encryptionData}
					setEncryptionData={setEncryptionData}
					requestTestError={requestTestError}
					setRequestTestError={setRequestTestError}
					parameters={parameters}
					setParameters={setParameters}
				/>
			)}
		</div>
	);
};

const ErrorLabel: React.FC<{ error: string }> = ({ error }) => {
	return (
		<span>
			<img
				style={{ marginRight: '0.5rem' }}
				src={ErrorIcon}
				alt="error icon"
			/>
			{error}
		</span>
	);
};

const EnabledContent: React.FC<EnabledContentProps> = ({
	encryptionData,
	setEncryptionData,
	requestTestError,
	setRequestTestError,
	parameters,
	setParameters,
}) => {
	const { t } = useTranslation();

	// Used to validate if the key passed in is valid
	// If the algorithm is still undefined, then the key pass the validation
	const isInvalidKey = () => {
		if (!isTouched('key')) return null;
		if (!encryptionData.algorithm?.data) return null;
		if (!encryptionData.key?.data)
			return <ErrorLabel error={t('keyRequired')} />;
		// Algorithm is something like XXX-NUMBER-XXX
		// So for retrievien the number we need to split it by '-' and get the middle one
		// That is the lenght of the key in bit, divide it by 8 and you have the bytes
		const errorMessage = validateKey(
			encryptionData.key?.data ?? '',
			encryptionData.algorithm?.data
		);

		if (!errorMessage) return null;

		const keyLength = +encryptionData.algorithm.data.split('-')[1] / 8;
		return <ErrorLabel error={t(errorMessage, { length: keyLength })} />;
	};

	// Used to validate the IV length
	// IV MUST be IV_LEGTH bytes long always
	const isInvalidIv = () => {
		if (!isTouched('iv')) return null;
		// If the algorithm or the IV value are still not filled
		// Then raise no error
		if (!encryptionData.algorithm) return null;
		if (!encryptionData.iv?.data)
			return <ErrorLabel error={t('ivRequired')} />;

		const errorMessage = validateIV(encryptionData.iv.data);
		if (!errorMessage) return null;

		return <ErrorLabel error={t(errorMessage)} />;
	};

	// Generic handle change function which takes as input the key that needs to be
	// changed and return a function which change the state of the parent with a callback
	const handleChange = (key: keyof EncryptionData,e?:unknown) => {
			let value: string;
			if (typeof e !== 'string') {
				value = (e as React.ChangeEvent<HTMLInputElement>)?.target
					?.value;
			} else value = e;
			setEncryptionData({ ...encryptionData, [key]: { data: value } });
	};

	const isTouched = (key: keyof EncryptionData) =>
		Boolean(encryptionData[key]?.touched);

	const setTouched = (key: keyof EncryptionData) => {
		setEncryptionData({
			...encryptionData,
			[key]: { data: encryptionData[key]?.data, touched: true },
		});
	};

	const handleAddParams = () => {
		setParameters((prev) => [...prev, '']);
	};

	const canIAddMoreParameters = () =>
		parameters.length !== 0 && parameters[parameters.length - 1] !== '';

	return (
		<div>
			<Select
				value={encryptionData.algorithm?.data}
				label={t('selAlgorithm')}
				placeholder={t('selAlgorithm')}
				onChange={(e)=>handleChange('algorithm',e)}
				className={Styles.mrgBtm}
				showBottomLabelSpace={false}
			>
				{algorithms.map((item) => (
					<SelectOption key={item.value} value={item.value}>
						{item.key}
					</SelectOption>
				))}
			</Select>
			<Input
				value={encryptionData.key?.data}
				onChange={(e)=>{
					handleChange('key',e);
					setRequestTestError({
						...requestTestError,
						key:''
					});
				}}
				label={t('enterKey')}
				placeholder={t('enterKey')}
				className={Styles.mrgBtm}
				onBlur={() => setTouched('key')}
				bottomLabelType={requestTestError.key === 'error' || isInvalidKey() !== null ? 'error' : undefined}
				bottomLabel={requestTestError.key === 'error'? <ErrorImage title={t('keyRequired')} /> : isInvalidKey()}
				showBottomLabelSpace={isInvalidKey() !== null}
			/>
			<Input
				value={encryptionData.iv?.data}
				onChange={(e)=>{
					setRequestTestError({
						...requestTestError,
						iv:''
					});
					handleChange('iv',e)}}
				label={t('enterIV')}
				placeholder={t('enterIV')}
				className={Styles.mrgBtm}
				onBlur={() => setTouched('iv')}
				bottomLabelType={requestTestError.iv === 'error' || isInvalidIv() !== null ? 'error' : undefined}
				bottomLabel={requestTestError.iv === 'error'? <ErrorImage title={t('ivRequired')} /> : isInvalidIv()}
				showBottomLabelSpace={isInvalidIv() !== null}
			/>
			<div className={Styles.compSubHeader}>{t('paramsToEncrypt')}</div>
			{parameters.length === 0 && (
				<ParamsToEncrypt
					value={''}
					index={0}
					key={0}
					setParameters={setParameters}
				/>
			)}
			{parameters.map((parameter, index) => (
				<ParamsToEncrypt
					value={parameter}
					index={index}
					key={index}
					setParameters={setParameters}
				/>
			))}
			<span
				className={Styles.addMoreContainer}
				onClick={canIAddMoreParameters() ? handleAddParams : undefined}
			>
				<img
					src={canIAddMoreParameters() ? AddIcon : AddIconDisabled}
					alt="add icon"
				/>
				<span
					className={cn(
						Styles.addMore,
						canIAddMoreParameters() ? Styles.enabled : ''
					)}
				>
					{t('addMore')}
				</span>
			</span>
		</div>
	);
};

const ParamsToEncrypt: React.FC<ParamsToEncrypt> = ({
	value,
	index,
	setParameters,
}) => {
	const { t } = useTranslation();

	const handleRemove = (index: number) => {
		setParameters((prev) => {
			if (prev.length === 1) return [];

			return prev.filter((_, i) => i !== index);
		});
	};

	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (
		event
	) => {
		setParameters((prev) => {
			const newArray = [...prev];
			newArray[index] = event.target.value;
			return newArray;
		});
	};

	return (
		<div className={Styles.parameters}>
			<Input
				className={Styles.parametersField}
				value={value}
				onChange={handleChange}
				label={t('key')}
				placeholder={t('key')}
				showBottomLabelSpace={false}
				key={index}
			/>
			<img
				className={Styles.closeIcon}
				src={CloseIcon}
				alt="close icon"
				onClick={() => handleRemove(index)}
			/>
		</div>
	);
};

export default EncProfile;
